class PromoBar extends HTMLElement {
  constructor() {
    super();
    this.itemsWrapper = this.querySelector('.swiper-wrapper');
    FoxTheme.Motion.inView(this, this.initializeCarousel.bind(this), { margin: '200px 0px 200px 0px' });
    
    this.setPromoBarHeight();
    this.checkTextOverflow();
    
    if (window.ResizeObserver) {
      new ResizeObserver(() => {
        this.setPromoBarHeight();
        this.checkTextOverflow();
      }).observe(this.promoBarSection);
    }
    
    this.initStickyBehavior();
  }

  get promoBarSection() {
    return this.closest('.promo-bar-section');
  }

  get isSticky() {
    return this.promoBarSection?.classList.contains('promo-bar-section--sticky');
  }

  initStickyBehavior() {
    if (!this.isSticky) return;
    
    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.promoBarBounds = this.promoBarSection.getBoundingClientRect();
    
    window.addEventListener('scroll', this.onScrollHandler.bind(this), false);
  }

  setPromoBarHeight() {
    if (!this.promoBarSection) return;
    
    requestAnimationFrame(() => {
      if (this.isSticky) {
        const height = Math.round(this.promoBarSection.offsetHeight);
        document.documentElement.style.setProperty('--promo-bar-height', `${height}px`);
      } else {
        document.documentElement.style.removeProperty('--promo-bar-height');
      }
    });
  }

  checkTextOverflow() {
    const textElement = this.querySelector('.promo-bar__text');
    const arrows = this.querySelectorAll('.promo-bar__nav-btn');
    const container = this.querySelector('.promo-bar');
    
    if (!textElement || !container) return;
    
    requestAnimationFrame(() => {
      // Reset to single line first to measure actual text width
      this.promoBarSection.classList.remove('promo-bar--multiline');
      
      const containerWidth = container.offsetWidth;
      
      // Calculate actual arrow space including their positioning
      let totalArrowSpace = 0;
      if (arrows.length > 0) {
        arrows.forEach(arrow => {
          totalArrowSpace += arrow.offsetWidth + 20; // arrow width + positioning padding
        });
      }
      
      const padding = 20; // Base padding
      const availableWidth = containerWidth - totalArrowSpace - padding;
      
      // Measure actual text width
      const textWidth = textElement.scrollWidth;
      
      if (textWidth > availableWidth && arrows.length > 0) {
        this.promoBarSection.classList.add('promo-bar--multiline');
      } else {
        this.promoBarSection.classList.remove('promo-bar--multiline');
      }
    });
  }

  onScrollHandler() {
    if (!this.isSticky) return;
    
    const scrollTop = window.scrollY;
    const promoBarSection = this.promoBarSection;
    const promoBarBoundsTop = this.promoBarBounds.top + this.firstScrollTop;
    
    requestAnimationFrame(() => {
      if (scrollTop > promoBarBoundsTop) {
        promoBarSection.classList.add('promo-bar-scrolled');
      } else {
        promoBarSection.classList.remove('promo-bar-scrolled');
      }
      
      this.currentScrollTop = scrollTop;
    });
  }

  getNextSlideButton() {
    return this.querySelector('.promo-bar__nav-btn--next') || this.querySelector('.swiper-button-next');
  }

  getPrevSlideButton() {
    return this.querySelector('.promo-bar__nav-btn--prev') || this.querySelector('.swiper-button-prev');
  }

  get getSlideItems() {
    return (this._cachedSlideItems = this._cachedSlideItems || Array.from(this.itemsWrapper?.children || []));
  }

  isAutoplayEnabled() {
    return this.getAttribute('data-autoplay') === 'true' || this.dataset.autoplay === 'true';
  }

  getAutoplaySpeed() {
    const raw = this.getAttribute('data-autoplay-speed') || this.dataset.autoplaySpeed || '5';
    const seconds = parseInt(raw, 10);
    return Number.isFinite(seconds) ? seconds * 1000 : 5000;
  }

  isNavigationEnabled() {
    return this.getAttribute('data-show-navigation') === 'true' || this.dataset.showNavigation === 'true';
  }

  initializeCarousel() {
    if (this.carousel || this._initializing) return;
    this._initializing = true;
  
    const slides = this.getSlideItems;
    if (slides.length <= 1) {
      const nav = this.querySelector('.promo-bar__navigation');
      if (nav) nav.style.display = 'none';
      this._initializing = false;
      return;
    }
  
    const autoplay = this.isAutoplayEnabled()
      ? {
          delay: this.getAutoplaySpeed(),
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }
      : false;
  
    const navigation = this.isNavigationEnabled()
      ? {
          nextEl: this.getNextSlideButton(),
          prevEl: this.getPrevSlideButton(),
        }
      : undefined;
  
    const options = {
      loop: true,
      autoplay,
      navigation,
      on: {
        slideChange: () => {
          const s = this.carousel?.slider;
          if (this.isAutoplayEnabled() && s?.params?.autoplay && !s.autoplay?.running) {
            try { s.autoplay.start(); } catch {}
          }
        },
      },
    };
  
    try {
      this.carousel = new FoxTheme.Carousel(this, options, FoxTheme.Swiper.Autoplay);
      this.carousel.init();
    } finally {
      this._initializing = false;
    }
  
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        this.carousel?.slider?.slideToLoop(slides.indexOf(event.target));
      });
    }
  }
}

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar);
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const promoBar = event.target.querySelector('promo-bar');
    if (promoBar) {
      setTimeout(() => {
        promoBar.initializeCarousel();
      }, 200);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const promoBar = event.target.querySelector('promo-bar');
    if (promoBar && promoBar.carousel && promoBar.carousel.slider) {
      try {
        promoBar.carousel.slider.destroy(true, true);
      } catch (e) {}
    }
  });
}